import {
  type CustomerFieldBindingConfidenceHint,
  type CustomerFieldBindingOverwritePolicy,
  type CustomerFieldBindingTransform,
  type CustomerFieldMappingContract,
  type CustomerFieldMappingGroup,
  type CustomerFieldMappingTarget,
  customerFieldTargetOptions,
} from '@/contracts/customers'
import type { Question, QuestionType } from '@/types/form'

export const customerFieldGroupLabels: Record<CustomerFieldMappingGroup, string> = {
  workflow: '流程控制',
  company: '公司資料',
  contact: '聯絡人',
  payment: '付款與規模',
  address: '地址資訊',
  receiving: '帳務與收貨',
  credit: '授信',
  sales: '業務',
  classification: '客戶分類',
  region: '區域資訊',
  notes: '其他欄位',
  legacy: '相容欄位',
}

export const customerFieldTransformLabels: Record<CustomerFieldBindingTransform, string> = {
  none: '不轉換',
  trim: '去除前後空白',
  number: '轉為數字',
  currency: '轉為金額',
  phone: '電話格式',
  email: 'Email 小寫化',
  url: '網址格式',
  date: '日期格式',
  join_choices: '多選合併文字',
}

export const customerFieldOverwritePolicyLabels: Record<
  CustomerFieldBindingOverwritePolicy,
  string
> = {
  prefill_if_empty: '空值才帶入',
  overwrite_manual: '覆寫手動值',
  ask_before_overwrite: '覆寫前詢問',
}

export const customerFieldConfidenceLabels: Record<CustomerFieldBindingConfidenceHint, string> = {
  exact: '精準',
  likely: '可能',
  manual_review: '需人工確認',
}

export function getCustomerFieldTargetLabel(
  targetField: CustomerFieldMappingTarget,
  display: 'full' | 'short' = 'full',
) {
  const option = getCustomerFieldTargetOption(targetField)
  if (!option) return targetField
  return display === 'short' ? option.shortLabel : option.label
}

export function getCustomerFieldTargetOption(targetField: CustomerFieldMappingTarget) {
  return customerFieldTargetOptions.find((item) => item.value === targetField)
}

export function defaultCustomerFieldTransform(
  type: QuestionType,
  targetField: CustomerFieldMappingTarget,
): CustomerFieldBindingTransform {
  const targetOption = getCustomerFieldTargetOption(targetField)
  if (targetField === 'contactEmail' || targetField === 'ownerEmail') return 'email'
  if (
    targetField === 'contactPhone' ||
    targetField === 'companyPhone' ||
    targetOption?.dbFieldName?.startsWith('phone_')
  ) {
    return 'phone'
  }
  if (targetField === 'website') return 'url'
  if (targetField === 'expectedValue' || targetField === 'creditLimit') return 'currency'
  if (type === 'NUMBER' || type === 'NUMBER_SLIDER' || type === 'TOTAL') return 'number'
  if (type === 'CHECKBOXES' || type === 'RANKING' || type === 'PICTURE_CHOICE') {
    return 'join_choices'
  }
  if (type === 'DATE' || type === 'TIME') return 'date'
  if (type === 'EMAIL') return 'email'
  if (type === 'PHONE_NUMBER' || type === 'TAIWAN_MOBILE' || type === 'TAIWAN_LANDLINE')
    return 'phone'
  return 'trim'
}

export function getQuestionSpecNumber(question: Question) {
  const raw = Number(question.settings.specQuestionNo)
  return Number.isFinite(raw) && raw > 0 ? raw : undefined
}

export function summarizeQuestionOptionRules(question: Pick<Question, 'choices' | 'settings'>) {
  const choices = question.choices.map((choice) => choice.label).filter(Boolean)
  if (choices.length === 0) return undefined
  const preview = choices.slice(0, 6).join('、')
  return choices.length > 6 ? `${preview} ... 共 ${choices.length} 個選項` : preview
}

export function summarizeQuestionValidationRules(
  question: Pick<Question, 'required' | 'settings'>,
) {
  const parts: string[] = []
  if (question.settings.specRequired === true || question.required) parts.push('必填')
  if (typeof question.settings.conditionalRequired === 'string') {
    parts.push(question.settings.conditionalRequired)
  }
  if (typeof question.settings.validation === 'string' && question.settings.validation !== 'none') {
    parts.push(`驗證：${question.settings.validation}`)
  }
  if (typeof question.settings.maxLength === 'number') {
    parts.push(`字數上限：${question.settings.maxLength}`)
  }
  if (typeof question.settings.placeholder === 'string' && question.settings.placeholder) {
    parts.push(`placeholder：${question.settings.placeholder}`)
  }
  if (typeof question.settings.dependentSource === 'string') {
    parts.push(`依 ${question.settings.dependentSource} 動態顯示`)
  }
  return parts.length > 0 ? parts.join('；') : undefined
}

export function buildCustomerFieldMappingFromQuestion({
  formId,
  question,
  targetField,
  previous,
}: {
  formId: string
  question: Question
  targetField: CustomerFieldMappingTarget
  previous?: CustomerFieldMappingContract
}): CustomerFieldMappingContract {
  const option = getCustomerFieldTargetOption(targetField)
  const specQuestionNo = getQuestionSpecNumber(question)
  return {
    id:
      previous?.id ??
      `map-${question.id}-${targetField}`.replace(/[^a-zA-Z0-9_-]/g, '-').toLocaleLowerCase(),
    formId,
    questionId: question.id,
    questionTitle: question.title,
    targetField,
    targetLabel: option?.shortLabel ?? option?.label ?? targetField,
    dbFieldName: option?.dbFieldName,
    dataType: option?.dataType,
    specQuestionNo,
    choiceRuleSummary: summarizeQuestionOptionRules(question),
    validationRuleSummary: summarizeQuestionValidationRules(question),
    transform: previous?.transform ?? defaultCustomerFieldTransform(question.type, targetField),
    requiredForApproval:
      previous?.requiredForApproval ??
      (typeof question.settings.specRequired === 'boolean'
        ? question.settings.specRequired
        : question.required),
    overwritePolicy: previous?.overwritePolicy ?? 'prefill_if_empty',
    confidenceHint: previous?.confidenceHint ?? 'exact',
    updatedAt: new Date().toISOString(),
  }
}

export function getCustomerFieldMappingConflictGroups(mappings: CustomerFieldMappingContract[]) {
  const grouped = new Map<CustomerFieldMappingTarget, CustomerFieldMappingContract[]>()
  for (const mapping of mappings) {
    grouped.set(mapping.targetField, [...(grouped.get(mapping.targetField) ?? []), mapping])
  }

  return Array.from(grouped.entries())
    .filter(([, items]) => items.length > 1)
    .map(([targetField, items]) => ({ targetField, items }))
}

export function getCustomerFieldMappingConflictAdvice(
  targetField: CustomerFieldMappingTarget,
  mappings: CustomerFieldMappingContract[],
) {
  const fieldLabel = getCustomerFieldTargetLabel(targetField, 'short')
  const titles = mappings.map((mapping) => mapping.questionTitle || '未命名題目')
  return `「${fieldLabel}」目前由 ${titles.join('、')} 同時帶入。建議只保留最可信的題目，其餘改綁其他欄位或取消 binding；若產品決定保留多題，後端 cutover 前必須定義優先順序與 audit log。`
}

export function getMappingConflictCount(
  targetField: CustomerFieldMappingTarget,
  mappings: CustomerFieldMappingContract[],
) {
  return mappings.filter((mapping) => mapping.targetField === targetField).length
}
